// src/pages/departments/LegalStudies.js
import { motion } from "framer-motion";

const LegalStudies = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden">
        <motion.img
          src="/images/legalb.jpeg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1 }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div
          className="relative text-center text-white px-6"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-3xl md:text-5xl font-extrabold mb-3">
            Department of Legal Studies
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            Upholding Justice, Integrity, and Academic Excellence
          </p>
        </motion.div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            The <strong>Department of Legal Studies</strong> at the{" "}
            <strong>Federal Polytechnic Bida</strong> was established to provide
            sound legal education and develop competent professionals who can
            apply legal principles across public, private, and corporate sectors.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            The department operates under the{" "}
            <strong>National Board for Technical Education (NBTE)</strong> and
            offers both <strong>National Diploma (ND)</strong> and{" "}
            <strong>Higher National Diploma (HND)</strong> programs. These
            programs aim to equip students with knowledge of civil law, criminal
            law, constitutional principles, legal drafting, and human rights
            education.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed">
            Through a blend of theoretical and practical training, the
            department produces graduates who contribute effectively to justice
            administration, business law, and governance.
          </p>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <motion.img
              src="/images/hodlegal.jpeg"
              alt="Barrister Aishatu Dangana Etsu"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Head of Department's Speech
            </h2>
            <motion.p
              className="text-justify text-gray-600 leading-relaxed mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              It gives me great pleasure to welcome you to the{" "}
              <strong>Department of Legal Studies</strong>,{" "}
              <strong>Federal Polytechnic Bida</strong>. Our department stands as
              a beacon of legal scholarship, discipline, and service to
              humanity.
            </motion.p>
            <motion.p
              className="text-justify text-gray-600 leading-relaxed mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              We are dedicated to nurturing future legal professionals who will
              uphold justice, demonstrate ethical integrity, and contribute
              meaningfully to national development. Through rigorous academic
              programs, practical training, and mentorship, we ensure that our
              students are well-equipped to meet the legal and administrative
              demands of modern society.
            </motion.p>
            <motion.p
              className="text-justify text-gray-600 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              As legal education continues to evolve, our department remains
              steadfast in promoting excellence, integrity, and professionalism
              — values that are at the core of our identity.
            </motion.p>
            <p className="mt-4 text-blue-700 font-semibold">
              — Barrister (Mrs.) Aishatu Dangana Etsu
            </p>
            <p className="text-gray-500">Head, Department of Legal Studies</p>
          </div>
        </motion.section>

        {/* Vision & Mission Section */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              To be a <strong>center of excellence</strong> in legal education,
              fostering the development of competent, disciplined, and ethical
              professionals who will uphold justice and promote the rule of law
              in Nigeria and beyond.
            </p>
          </div>
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              To provide <strong>quality legal education and training</strong>{" "}
              that equips students with the knowledge, analytical skills, and
              ethical standards required to function effectively in the legal,
              administrative, and judicial sectors of society.
            </p>
          </div>
        </motion.section>

        {/* Program Overview & Career Opportunities */}
        <motion.section
          className="bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Program Overview & Career Opportunities
          </h2>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            The Department of Legal Studies provides a solid foundation in
            legal systems, ethics, and procedures. Students are trained to
            understand and apply the principles of law to real-life situations
            through case studies, mock trials, and court internships. The
            curriculum emphasizes both academic and practical exposure to help
            students excel in various legal and administrative capacities.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            Graduates of the department can pursue rewarding careers in:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4">
            <li>Law Firms and Legal Chambers</li>
            <li>Judiciary and Court Administration</li>
            <li>Government Ministries and Parastatals</li>
            <li>Corporate Legal and Compliance Departments</li>
            <li>Law Enforcement and Regulatory Agencies</li>
            <li>Human Rights and Advocacy Organizations</li>
            <li>Academic and Research Institutions</li>
          </ul>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Entry Requirements
          </h3>
          <p className="text-justify text-gray-600 leading-relaxed">
            Candidates must possess <strong>five (5) O’Level credits</strong> in
            English Language, Mathematics, Government, and any two of Economics,
            Literature in English, or Civic Education, obtained in not more than
            two sittings. A minimum UTME score as specified by NBTE is required
            for admission into the <strong>ND program</strong>, which runs for
            two academic years (four semesters).
          </p>
        </motion.section>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Department Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["legal1.jpeg", "legal2.jpeg", "legal3.png", "legal4.jpeg"].map(
              (img, i) => (
                <motion.img
                  key={i}
                  src={`/images/${img}`}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-40 md:h-48 object-cover rounded-lg shadow-md"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
              )
            )}
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Department
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> dept.legalstudies@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 703 351 6447
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.278884327274!2d6.0125!3d9.0820!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104965e46b2e11f5%3A0xdeadbeef123456!2sFederal%20Polytechnic%20Bida!5e0!3m2!1sen!2sng!4v0000000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default LegalStudies;
